import React, { Fragment, useCallback, useMemo, useState } from "react";

import cn from "classnames";
import uniqBy from "lodash-es/uniqBy";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";

import RoleEnum from "@mapmycustomers/shared/enum/Role";
import User from "@mapmycustomers/shared/types/User";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import { Modal, SearchField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import ToggleEntityCheckbox from "@app/component/input/ToggleEntityCheckbox";
import useMutableSet from "@app/util/hook/useMutableSet";
import { getRoleDisplayName } from "@app/util/ui";

import { SelectEntityDialogProps } from "./selectEntityDialogFactory";
import styles from "./selectEntityDialogFactory.module.scss";
import defaultFilter from "./util/defaultFilter";

const noUsersMessage = defineMessage({
  id: "selectUserDialogModal.users.noEntries",
  defaultMessage: "No Users Found",
  description: "'No entries' message for the select users dialog",
});

interface TeamWithMembers {
  id?: number;
  isNoTeam?: boolean;
  isOwners: boolean;
  members: User[];
  name?: string;
}

const SelectUsersDialog: React.FC<SelectEntityDialogProps<User>> = ({
  cancelButtonLabel,
  confirmButtonLabel,
  entities,
  filter,
  getDisplayName,
  getKey,
  onCancel,
  onConfirm,
  onHide,
  selectedEntities = [],
  title,
  visible,
}) => {
  // filtering-related stuff
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const filteredEntities = useMemo(() => {
    if (filter === null) {
      return entities;
    }
    return filter
      ? filter(entities, searchQuery)
      : defaultFilter(entities, searchQuery, getDisplayName);
  }, [entities, filter, searchQuery, getDisplayName]);

  // checking-related stuff
  const [checkedEntities, updateCheckedEntities, handleToggleEntity] = useMutableSet<User>(
    () => new Set(selectedEntities)
  );

  const handleSelectAllCheckboxChange = useCallback(
    (checked) =>
      updateCheckedEntities((set) => {
        filteredEntities.forEach((entity) => {
          if (checked) {
            set.add(entity);
          } else {
            set.delete(entity);
          }
        });
        return true;
      }),
    [filteredEntities, updateCheckedEntities]
  );

  const groupedUsers: TeamWithMembers[] = useMemo(() => {
    const teams = uniqBy(
      filteredEntities.flatMap((user) => user.teams),
      "id"
    ).sort(nameComparator);

    return [
      {
        isOwners: true,
        members: filteredEntities.filter(({ role }) => role.key === RoleEnum.OWNER),
      },
      ...teams.map(({ id, name }) => ({
        id,
        isOwners: false,
        members: filteredEntities.filter(
          ({ role, teams }) => role.key !== RoleEnum.OWNER && teams.some((team1) => team1.id === id)
        ),
        name,
      })),
      {
        isNoTeam: true,
        isOwners: false,
        members: filteredEntities.filter(
          ({ role, teams }) => role.key !== RoleEnum.OWNER && teams.length === 0
        ),
        name: intl.formatMessage({
          id: "selectUserDialogModal.users.noTeam",
          defaultMessage: "No Team",
          description: "No Team message for the select users dialog",
        }),
      },
    ];
  }, [filteredEntities, intl]);

  const allSelected = filteredEntities.every((entity) => checkedEntities.has(entity));
  const isFiltering = filteredEntities.length < entities.length;
  const selectAllLabel =
    (allSelected
      ? intl.formatMessage({
          id: "selectUserDialogModal.users.deselectAll",
          defaultMessage: "Deselect All",
          description: "Deselect all message for the select users dialog",
        })
      : intl.formatMessage({
          id: "selectUserDialogModal.users.selectAll",
          defaultMessage: "Select All",
          description: "Select all message for the select users dialog",
        })) +
    (isFiltering
      ? intl.formatMessage({
          id: "selectUserDialogModal.users.filterTitle",
          defaultMessage: " Users",
          description: "Title message for the select users dialog",
        })
      : "");

  const handleConfirmClick = useCallback(() => {
    onHide();
    onConfirm?.(Array.from(checkedEntities));
  }, [onConfirm, onHide, checkedEntities]);

  const getCheckedMembersCount = useCallback(
    (users: User[]) => {
      return users.filter((user) => checkedEntities.has(user)).length;
    },
    [checkedEntities]
  );

  const handleSelectTeam = useCallback(
    (users: User[], select: boolean) => {
      users.map((user) => handleToggleEntity(user, select));
    },
    [handleToggleEntity]
  );

  return (
    <Modal
      cancelText={
        cancelButtonLabel ??
        intl.formatMessage({
          id: "selectUserDialogModal.default.cancelButton",
          defaultMessage: "Cancel",
          description: "Placeholder for cancel button text for modal in select user dialog factory",
        })
      }
      okText={
        confirmButtonLabel ??
        intl.formatMessage({
          id: "selectUserDialogModal.default.okButton",
          defaultMessage: "Done",
          description: "Placeholder for ok button text for modal in select user dialog factory",
        })
      }
      onCancel={onCancel || onHide}
      onOk={handleConfirmClick}
      title={title}
      visible={visible}
    >
      <Checkbox
        checked={allSelected}
        indeterminate={
          allSelected ? false : filteredEntities.some((entity) => checkedEntities.has(entity))
        }
        onChange={handleSelectAllCheckboxChange}
      >
        {selectAllLabel}
      </Checkbox>
      {filter !== null && (
        <SearchField
          className={styles.searchInput}
          onChange={setSearchQuery}
          placeholder={intl.formatMessage({
            id: "selectUserDialogModal.default.search.placeholder",
            defaultMessage: "Search",
            description: "Placeholder for search select entity dialog factory",
          })}
          value={searchQuery}
        />
      )}
      <div className={styles.container}>
        {groupedUsers
          .filter(({ isOwners }) => isOwners)
          .map((team) => (
            <Fragment
              key={team.id ?? team.isOwners ? "owners" : team.isNoTeam ? "noTeam" : "foobar"}
            >
              {team.members.map((member) => (
                <ToggleEntityCheckbox
                  checked={checkedEntities.has(member)}
                  displayName={intl.formatMessage(
                    {
                      id: "selectUserDialogModal.default.search.userCheckbox",
                      defaultMessage: "{name}{isRep, select, true {} other { ({roleName})}}",
                      description: "Checkbox for search select entity dialog factory",
                    },
                    {
                      isRep: member.role.key === RoleEnum.MEMBER,
                      name: getDisplayName(member),
                      roleName: getRoleDisplayName(intl, member.role.key),
                    }
                  )}
                  entity={member}
                  key={getKey(member)}
                  onToggle={handleToggleEntity}
                />
              ))}
            </Fragment>
          ))}
        {groupedUsers
          .filter(({ isOwners }) => !isOwners)
          .map((team) => {
            const selectedCount = getCheckedMembersCount(team.members);
            const allSelected = selectedCount === team.members.length;
            const someSelected = !allSelected && selectedCount > 0;
            return (
              <Fragment
                key={team.id ?? team.isOwners ? "owners" : team.isNoTeam ? "noTeam" : "foobar"}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => handleSelectTeam(team.members, !allSelected)}
                >
                  <span className={cn({ [styles.noTeam]: !!team.isNoTeam })}>{team.name}</span>
                </Checkbox>
                {team.members.map((member) => (
                  <ToggleEntityCheckbox
                    checked={checkedEntities.has(member)}
                    className={styles.nestedItem}
                    displayName={`${getDisplayName(member)} ${
                      member.role.key !== RoleEnum.MEMBER
                        ? `(${getRoleDisplayName(intl, member.role.key)})`
                        : ""
                    }`}
                    entity={member}
                    key={getKey(member)}
                    onToggle={handleToggleEntity}
                  />
                ))}
              </Fragment>
            );
          })}

        {!filteredEntities.length && (
          <div className={styles.noEntityMessage}>
            <div className={styles.noEntityMessage}>
              <FormattedMessage {...noUsersMessage} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SelectUsersDialog;
